import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "@/components/Container";
import { ContentReviewCard } from "@/components/ContentReviewCard";
import {
  ContentToReview,
  useDeleteReviewContentMutation,
  usePerformActionOnReviewContentMutation,
  useReviewContentQuery,
} from "@/api/content-review";
import { usePersonasQueryNoPublic } from "@/api/personas";
import { contentReviewerFiltersSchema } from "@/lib/content-reviewer-filters";
import { useTypedSearchParams } from "@/hooks/use-typed-search-params";
import { useToast } from "@/hooks/use-toast.ts";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { LoaderCircle, ChevronsUpDown, Check } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { uploadImage } from "@/api/api";
import { useQueryClient } from "@tanstack/react-query";

const EmptyStateModal = () => {
  const navigate = useNavigate();

  return (
    <Dialog open={true} onOpenChange={() => navigate("/")}>
      <DialogContent className="bg-black/100 border-white/10 border-border md:max-w-[50%] w-[40vw] h-[75vh]">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            Create an Agent to then review its AI generated images
          </DialogTitle>
          <DialogDescription className="text-white/60 text-lg">
            To create, review and manage image content, you'll need to create an
            agent first
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center h[20%] border-border">
          <img
            src="/guidance-images/content-reviewer.png"
            alt="Guidance"
            className="h-[45vh] object-contain"
          />
        </div>
        <div className="flex justify-center mt-2">
          <Button
            className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3"
            onClick={() => navigate("/agents/new?returnUrl=/content-review")}
          >
            Create Agent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function ContentReviewer() {
  const navigate = useNavigate();

  const {
    data: personas = [],
    isError,
    isLoading: isPersonasLoading,
  } = usePersonasQueryNoPublic();
  const [selectedPersona, setSelectedPersona] = useSelectedPersona();
  const [filters, setFilters] = useTypedSearchParams(
    contentReviewerFiltersSchema,
    {
      order_by: "timestamp",
    },
  );
  const {
    isFetching: isContentLoading,
    isSuccess: isContentSuccess,
    data,
    fetchNextPage,
  } = useReviewContentQuery({ ...filters, persona: selectedPersona });
  const { mutate: deleteReviewContent } = useDeleteReviewContentMutation();
  const { mutate: performActionOnReviewContent } =
    usePerformActionOnReviewContentMutation();
  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Only show the empty state modal when we've finished loading and know there are no personas
  const shouldShowEmptyState = !isPersonasLoading && personas.length === 0;

  useEffect(() => {
    if (isError) {
      toast({ description: "Error loading personas" });
    }
  }, [isError, toast]);

  useEffect(() => {
    if (!contentContainer || isContentLoading) return;

    function onIntersect(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      });
    }

    const observer = new IntersectionObserver(onIntersect, {
      /* As soon as 1px is visible, we'll trigger the callback. */
      threshold: 0,
    });

    observer.observe(contentContainer);

    return () => observer.disconnect();
  }, [contentContainer, isContentLoading, fetchNextPage]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadImage(file, selectedPersona);
        queryClient.invalidateQueries({
          queryKey: ["review-content", { persona: selectedPersona }],
        });
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  const handleDelete = async (item: ContentToReview) =>
    new Promise<void>((resolve, reject) =>
      deleteReviewContent(item.doc_id, {
        onSuccess: () => {
          toast({ description: "Content deleted successfully!" });
          resolve();
        },
        onError: (error) => {
          console.error("Error deleting content:", error);
          reject(error);
        },
      }),
    );

  const handleFilterChange = (name: string, value: Date | string | null) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]:
        value === ""
          ? null
          : value instanceof Date && !isNaN(value.getTime())
            ? value.toISOString()
            : value,
    }));
  };

  const handleActionClick = async (docId: string, updateData: object) =>
    new Promise<void>((resolve, reject) => {
      performActionOnReviewContent(
        { docId, data: updateData },
        {
          onError: (error) => {
            console.error("Error updating content:", error);
            reject(error);
          },
          onSuccess: () => resolve(),
        },
      );
    });

  const handlePersonaChange = (value: string) => {
    setPersonaOpen(false);
    setSelectedPersona(value);
  };

  function AdvancedFilters() {
    return (
      <div
        className={`flex flex-row gap-y-2 gap-x-2 ${filtersOpen ? "flex-col" : "flex-row"}`}
      >
        <Select
          value={filters.status ?? ""}
          onValueChange={(value) => handleFilterChange("status", value)}
        >
          <Label className="w-full">
            Status
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
          </Label>
          <SelectContent>
            <SelectItem value={null!}>
              <em>None</em>
            </SelectItem>
            <SelectItem value="image_generated">Image Generated</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="posted">Published</SelectItem>
          </SelectContent>
        </Select>
        <Label className="w-full">
          Start Date
          <DatePicker
            className="mt-2"
            placeholder="Start Date"
            value={
              filters?.start_date
                ? new Date(Date.parse(filters.start_date))
                : undefined
            }
            onChange={(date) => {
              if (!date || isNaN(date.getTime())) {
                handleFilterChange("start_date", null);
                setStartDateError(null);
              } else if (
                filters.end_date &&
                new Date(filters.end_date) < date
              ) {
                setStartDateError("Start date cannot be after end date.");
              } else {
                handleFilterChange("start_date", date);
                setStartDateError(null);
              }
            }}
          />
          {startDateError && (
            <p className="mt-2 text-red-500 text-xs">{startDateError}</p>
          )}
        </Label>
        <Label className="w-full">
          End Date
          <DatePicker
            className="mt-2"
            placeholder="End Date"
            value={
              filters.end_date
                ? new Date(Date.parse(filters.end_date))
                : undefined
            }
            onChange={(date) => {
              if (!date || isNaN(date.getTime())) {
                handleFilterChange("end_date", null);
                setEndDateError(null);
              } else if (
                filters.start_date &&
                new Date(filters.start_date) > date
              ) {
                setEndDateError("End date cannot be before start date.");
              } else {
                handleFilterChange("end_date", date);
                setEndDateError(null);
              }
            }}
          />
          {endDateError && (
            <p className="mt-2 text-red-500 text-xs">{endDateError}</p>
          )}
        </Label>
        <Select
          value={filters.order_by ?? ""}
          onValueChange={(value) => handleFilterChange("order_by", value)}
        >
          <Label className="w-full">
            Sort By
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
          </Label>
          <SelectContent>
            <SelectItem value="timestamp">
              <em>Most recent</em>
            </SelectItem>
            <SelectItem value="ai_rating">AI Rating</SelectItem>
            <SelectItem value="rating">Human Rating</SelectItem>
          </SelectContent>
        </Select>
        {/* <Label className="flex flex-col gap-y-2 ml-2">
          View Mode
          <RadioGroup
            className="flex flex-row gap-x-4 items-center h-[36px]"
            value={viewMode}
            onValueChange={(value) =>
              setViewMode(value === "video" ? "video" : "image")
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="image" id="image" />
              <Label htmlFor="image">Image</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="video" id="video" />
              <Label htmlFor="video">Video</Label>
            </div>
          </RadioGroup>
        </Label> */}
      </div>
    );
  }

  return (
    <>
      {shouldShowEmptyState ? (
        <EmptyStateModal />
      ) : (
        <div className="flex flex-col">
          <Container className="w-full">
            <div className="flex flex-col justify-between sm:flex-row items-start gap-x-2 gap-y-4">
              <div className="w-full sm:w-auto">
                <div className="w-full sm:w-auto flex flex-col gap-y-2">
                  <Label>Agent</Label>
                  <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="persona-selector-visuals"
                        data-testid="persona-selector-visuals"
                        variant="outline"
                        role="combobox"
                        aria-expanded={personaOpen}
                        className="w-48 px-8 py-2"
                      >
                        <span className="w-full sm:max-w-48 truncate">
                          {selectedPersona
                            ? personas.find(
                                (persona) =>
                                  persona.persona_id === selectedPersona,
                              )?.name
                            : "All"}
                        </span>
                        <ChevronsUpDown className="h-5 w-3 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                      <Command>
                        <CommandInput placeholder="Search persona..." />
                        <CommandList>
                          <CommandEmpty>No persona found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              key="all"
                              value="all"
                              onSelect={() => handlePersonaChange("")}
                              keywords={["all, none"]}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  !selectedPersona
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              All
                            </CommandItem>
                            {personas
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((persona) => (
                                <CommandItem
                                  key={persona.persona_id}
                                  value={persona.persona_id}
                                  onSelect={() =>
                                    handlePersonaChange(persona.persona_id)
                                  }
                                  keywords={[persona.name]}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedPersona === persona.persona_id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {persona.name}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="w-full sm:gap-x-4 flex justify-between flex-row gap-y-2">
                <div className="hidden md:block">
                  <AdvancedFilters />
                </div>
                <div className="w-full block md:hidden">
                  <Button onClick={() => setFiltersOpen(!filtersOpen)}>
                    {filtersOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  {filtersOpen && (
                    <div className="mt-4">
                      <AdvancedFilters />
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-x-2 gap-y-2">
                  <Button
                    className="bg-primary hover:bg-primary-dark flex items-center justify-center gap-2 sm:mt-5 sm:ml-auto whitespace-nowrap"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    UPLOAD IMAGE
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                    />
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary-dark flex items-center justify-center gap-2 sm:mt-5 sm:ml-auto whitespace-nowrap"
                    onClick={() =>
                      navigate(`/create/images?p=${selectedPersona ?? ""}`)
                    }
                  >
                    + Create Images
                  </Button>
                </div>
              </div>
            </div>
          </Container>
          {isContentLoading && (
            <div className="flex justify-center ">
              <LoaderCircle className="absolute w-6 h-auto animate-spin text-primary" />
            </div>
          )}
          {isContentSuccess &&
          (data.pages.length === 0 || data.pages[0].data.length === 0) ? (
            <div className="flex justify-center py-4 mt-8">
              <p className="text-center text-muted-foreground">
                No content found.
              </p>
            </div>
          ) : null}
          <div className="overflow-y-auto w-full">
            <Container>
              <div className="max-w-[250px] mx-auto sm:max-w-none">
                <div className="relative grid mt-8 gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-5 xl:grid-cols-6">
                  {personas.length > 0 &&
                    data?.pages.map((page) =>
                      page.data.map((item) => {
                        const persona = personas.find(
                          (persona) => persona.persona_id === item.persona_id,
                        );

                        return persona ? (
                          <ContentReviewCard
                            key={item.doc_id}
                            item={item}
                            persona={persona}
                            viewMode="image"
                            onDelete={() => handleDelete(item)}
                            onApprove={() =>
                              handleActionClick(item.doc_id, {
                                status: "approved",
                              })
                            }
                            onRate={(newValue) =>
                              handleActionClick(item.doc_id, {
                                rating: newValue,
                              })
                            }
                          />
                        ) : undefined;
                      }),
                    )}
                  <div
                    /* This div only exists so we can know when it's visible on screen, so we can start fetching the next page.
                     * You can adjust its height to tweak how soon or late you want the next page to start fetching. */
                    ref={setContentContainer}
                    className="absolute bottom-0 left-0 right-0 h-[1px]"
                  />
                </div>
              </div>
            </Container>
          </div>
        </div>
      )}
    </>
  );
}

export default ContentReviewer;
